import { useLocation } from 'react-router-dom';
import { Bell, ChevronDown, Building2, LogOut, User, Settings, Menu, Download, FileText, FileSpreadsheet, Archive, Trash2 } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { notifications } from '@/data/mockData';
import { useDownloadHistory } from '@/context/DownloadHistoryContext';
import { useDemoTour } from '@/context/DemoTourContext';

const routeNames = {
  '/dashboard': 'AI Security Dashboard',
  '/dashboard/incident-review': 'Incident Review',
  '/dashboard/risk-score': 'Risk Score',
  '/dashboard/ai-inventory': 'AI Inventory',
  '/dashboard/agent-controls': 'Agent Controls',
  '/dashboard/change-control': 'Change Control',
  '/dashboard/decision-ledger': 'Decision Ledger',
  '/dashboard/evidence-vault': 'Evidence Vault',
  '/dashboard/lineage': 'Lineage',
  '/dashboard/compliance': 'Proof Status',
  '/dashboard/proof-chain': 'Proof Chain',
  '/dashboard/assurance': 'Assurance Center',
  '/dashboard/regulatory-moment': 'Regulatory Moment of Truth',
  '/dashboard/roles': 'Roles & Responsibilities',
  '/dashboard/exceptions': 'Exceptions & Approvals',
  '/dashboard/integrations': 'Integrations',
  '/dashboard/team': 'Team',
  '/dashboard/settings': 'Settings',
};

const severityColors = {
  critical: '#EF4444',
  warning: '#F59E0B',
  info: '#3B82F6',
};

export default function TopBar({ onMobileMenuToggle }) {
  const location = useLocation();
  const currentPage = routeNames[location.pathname] || 'Overview';
  const { history, clearHistory } = useDownloadHistory();
  const { start: startDemo } = useDemoTour();

  const formatIcon = (format) => {
    if (format === 'csv') return <FileSpreadsheet size={12} className="text-[#10B981]" />;
    if (format === 'zip') return <Archive size={12} className="text-[#F59E0B]" />;
    return <FileText size={12} className="text-[#6366F1]" />;
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <header
      data-testid="topbar"
      className="h-14 lg:h-16 bg-[#080D1A]/90 backdrop-blur-md border-b border-[#1E2A45] flex items-center justify-between px-3 sm:px-4 lg:px-6 flex-shrink-0"
    >
      {/* Left: Hamburger + Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <button
          data-testid="mobile-menu-btn"
          onClick={onMobileMenuToggle}
          className="lg:hidden p-1.5 rounded-lg hover:bg-[#0F1629] text-[#94A3B8]"
        >
          <Menu size={20} />
        </button>
        <span className="text-[#64748B] hidden sm:inline">Apex</span>
        <span className="text-[#1E2A45] hidden sm:inline">/</span>
        <span className="text-[#F8FAFC] font-medium text-xs sm:text-sm">{currentPage}</span>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Start Demo button */}
        <button
          data-testid="start-demo-btn"
          onClick={startDemo}
          className="hidden sm:flex items-center gap-1.5 bg-[#6366F1] hover:bg-[#818cf8] text-white text-[11px] font-semibold px-3 py-1.5 rounded-full transition-all shadow-[0_0_12px_rgba(99,102,241,0.25)]"
        >
          <span className="text-xs">&#9654;</span> Start Demo
        </button>

        {/* Proof Chain Status Indicator */}
        <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#10B981]/20 bg-[#10B981]/5">
          <span className="text-[10px] font-mono text-[#10B981]">Proof Chain: ◆ AUDIT-GRADE</span>
        </div>

        {/* Org Selector - hidden on small mobile */}
        <div
          data-testid="org-selector"
          className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#1E2A45] text-sm cursor-pointer hover:border-[#6366F1]/30 transition-colors"
        >
          <Building2 size={14} className="text-[#6366F1]" />
          <span className="text-[#F8FAFC]">Meridian Global</span>
          <span className="text-[#64748B] hidden lg:inline">— Zurich · Production</span>
          <ChevronDown size={14} className="text-[#64748B]" />
        </div>

        {/* Download History */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="download-history-btn"
              className="relative p-2 rounded-lg hover:bg-[#0F1629] transition-colors"
            >
              <Download size={18} className="text-[#94A3B8]" />
              {history.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#6366F1] rounded-full text-[9px] font-bold text-white flex items-center justify-center">
                  {history.length > 9 ? '9+' : history.length}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-96 bg-[#0F1629] border-[#1E2A45]"
            data-testid="download-history-panel"
          >
            <div className="flex items-center justify-between px-2 py-1.5">
              <DropdownMenuLabel className="text-[#F8FAFC] text-sm p-0">Download History</DropdownMenuLabel>
              {history.length > 0 && (
                <button onClick={(e) => { e.stopPropagation(); clearHistory(); }} className="text-[9px] text-[#475569] hover:text-[#94A3B8] transition-colors flex items-center gap-1">
                  <Trash2 size={10} /> Clear
                </button>
              )}
            </div>
            <DropdownMenuSeparator className="bg-[#1E2A45]" />
            {history.length === 0 ? (
              <div className="px-3 py-6 text-center">
                <Download size={24} className="text-[#1E2A45] mx-auto mb-2" />
                <div className="text-xs text-[#475569]">No exports yet this session</div>
                <div className="text-[10px] text-[#1E2A45] mt-1">Generate a package to see it here</div>
              </div>
            ) : (
              <ScrollArea className="max-h-72">
                {history.map((entry) => (
                  <DropdownMenuItem
                    key={entry.id}
                    data-testid={`download-entry-${entry.id}`}
                    className="flex items-start gap-3 py-2.5 px-3 cursor-default focus:bg-[#1E2A45]"
                  >
                    <div className="w-7 h-7 rounded-lg bg-[#080D1A] border border-[#1E2A45] flex items-center justify-center flex-shrink-0 mt-0.5">
                      {formatIcon(entry.format)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] text-[#F8FAFC] font-medium truncate">{entry.filename}</div>
                      <div className="text-[10px] text-[#64748B] truncate">{entry.source}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[9px] text-[#475569]">{formatTime(entry.timestamp)}</span>
                        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#080D1A] text-[#64748B] uppercase">{entry.format}</span>
                      </div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </ScrollArea>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="notification-bell"
              className="relative p-2 rounded-lg hover:bg-[#0F1629] transition-colors"
            >
              <Bell size={18} className="text-[#94A3B8]" />
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#EF4444] rounded-full text-[9px] font-bold text-white flex items-center justify-center">
                3
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-80 bg-[#0F1629] border-[#1E2A45]"
          >
            <DropdownMenuLabel className="text-[#F8FAFC] text-sm">Notifications</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#1E2A45]" />
            {notifications.map((n) => (
              <DropdownMenuItem
                key={n.id}
                data-testid={`notification-${n.id}`}
                className="flex items-start gap-3 py-3 cursor-pointer focus:bg-[#1E2A45]"
              >
                <div
                  className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                  style={{ backgroundColor: severityColors[n.type] }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#F8FAFC] truncate">{n.title}</div>
                  <div className="text-xs text-[#64748B]">{n.desc}</div>
                  <div className="text-[10px] text-[#475569] mt-1">{n.time}</div>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Avatar */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-avatar"
              className="w-8 h-8 rounded-full bg-[#6366F1]/20 flex items-center justify-center text-xs font-semibold text-[#6366F1] hover:bg-[#6366F1]/30 transition-colors"
            >
              JM
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-48 bg-[#0F1629] border-[#1E2A45]"
          >
            <DropdownMenuLabel className="text-[#F8FAFC]">Jean-Michel</DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#1E2A45]" />
            <DropdownMenuItem className="text-[#94A3B8] focus:bg-[#1E2A45] focus:text-[#F8FAFC] cursor-pointer">
              <User size={14} className="mr-2" /> Profile
            </DropdownMenuItem>
            <DropdownMenuItem className="text-[#94A3B8] focus:bg-[#1E2A45] focus:text-[#F8FAFC] cursor-pointer">
              <Settings size={14} className="mr-2" /> Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[#1E2A45]" />
            <DropdownMenuItem className="text-[#EF4444] focus:bg-[#1E2A45] focus:text-[#EF4444] cursor-pointer">
              <LogOut size={14} className="mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
